import React, { useState, useEffect } from 'react';
import { AmortizationType } from '../LoanConstants';
import { LoanApi } from '@apply/api';
import { applicationInsights } from '../../../ApplicationInsights';
import { addMonths, formatCurrency } from '../../../common/SharedFunctions';
import DataGrid, { Column, Pager, Paging } from 'devextreme-react/data-grid';
import Dialog from '@material-ui/core/Dialog';
import { formatDate } from '../../../common/SharedFunctions';
import LoadPanel from 'devextreme-react/load-panel';

export const AmortizationTable = ({ loanId, amortizationTypeId, noteDate, interestRate, commitment }) => {
    const loanApi = new LoanApi.LoanApi(applicationInsights);

    const pageSize = 20;
    const [currentBalance, set_currentBalance] = useState(0);
    const [table, set_table] = useState(null);
    const [showTable, set_showTable] = useState(false);
    const [isLoadingOpen, set_isLoadingOpen] = useState(false);
    const [pageIndex, set_pageIndex] = useState(0);

    const styles = {
        buttonMarginRight: { marginRight: '10px' },
        buttonContainer: {
            textAlign: 'right',
            borderTop: '2px solid silver',
            padding: '10px 10px',
        },
        table_button: {
            marginLeft: '1px',
            height: '37px',
            width: '50px',
            borderRadius: '5px',
        },
    };

    useEffect(() => {
        calculateAmortization();
    }, [loanId, amortizationTypeId, noteDate, interestRate, commitment]);

    const _handleClose = () => {
        set_showTable(false);
    };

    const onShowTable = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        calculateAmortization();

        set_showTable(true);
        set_isLoadingOpen(false);
    };

    const calculateAmortization = () => {
        if (!canShowTable()) {
            return;
        }

        // if (table) {
        //   set_showTable(true);
        //   return;
        // }

        if (showTable) {
            set_isLoadingOpen(true);
        }

        try {
            let years = 0;
            switch (amortizationTypeId) {
                case AmortizationType.Type15:
                    years = 15;
                    break;
                case AmortizationType.Type20:
                    years = 20;
                    break;
                case AmortizationType.Type25:
                    years = 25;
                    break;
                case AmortizationType.Type30:
                    years = 30;
                    break;
                default:
                    set_table(0);
            }

            if (years > 0) {
                GenerateTableEntries(years, noteDate, commitment, interestRate);
            }
        } finally {
            set_isLoadingOpen(false);
        }
    };

    const GetExtraPayment = (pmtNo, paymentDate, repayments) => {
        if (!repayments || repayments.length === 0) {
            return 0;
        }

        const previousPaymentDate = addMonths(paymentDate, -1);

        let extraPayment = 0;
        for (let i = 0; i < repayments.length; i++) {
            const payDate = new Date(repayments[i].payDate);
            if (pmtNo === 1 && payDate <= paymentDate) {
                extraPayment += repayments[i].amount;
            } else if (payDate > previousPaymentDate && payDate <= paymentDate) {
                extraPayment += repayments[i].amount;
            }
        }
        return extraPayment;
    };

    const GenerateTableEntries = async (years, startDate, commitment, interestRate) => {
        const response = await loadRepayments(loanId);
        const repayments = response.result;

        let balance = commitment;
        let curBalance = commitment;
        const periods = 12 * years;
        const monthlyRate = interestRate / 100 / 12; // 0.065= APR of 6.5% as decimal
        let monthlyPayment = (monthlyRate / (1 - Math.pow(1 + monthlyRate, -periods))) * balance;
        const currentDate = new Date();

        const result = [];
        let paymentDate = addMonths(startDate, 1);
        let lastBalance = commitment;
        let totalInterest = 0;

        for (let x = 0; x < periods; x++) {
            let interestForMonth = balance * monthlyRate;
            let principalForMonth = monthlyPayment - interestForMonth;
            balance -= principalForMonth;
            totalInterest += interestForMonth;

            const pmtNo = x + 1;
            const nextPaymentDate = addMonths(paymentDate, 1);
            const isCurrentMonth = currentDate > paymentDate && currentDate <= nextPaymentDate;
            if (isCurrentMonth) {
                const index = Math.floor(pmtNo / pageSize);
                set_pageIndex(index);
            }

            const extraPayment = GetExtraPayment(pmtNo, paymentDate, repayments);
            if (extraPayment > 0) {
                principalForMonth += extraPayment;
                balance -= extraPayment;
            }
            if (balance < 0) {
                monthlyPayment += balance;
                balance = 0;
            }

            const entry = {
                pmtNo,
                pmtDate: paymentDate,
                startBalance: lastBalance,
                pmt: monthlyPayment,
                extraPmt: extraPayment,
                principal: principalForMonth,
                interest: interestForMonth,
                endBalance: balance,
                cumInterest: totalInterest,
                isCurrentMonth,
            };
            result.push(entry);

            if (isCurrentMonth) {
                curBalance = entry.endBalance;
            }

            if (balance <= 0) {
                break;
            }

            lastBalance = balance;
            paymentDate = nextPaymentDate;
        }

        set_currentBalance(formatCurrency(curBalance, 2));
        set_table(result);
    };

    const loadRepayments = async (loanId) => {
        const data = await loanApi.getRepaymentsSummaryForLoan(loanId);
        return data;
    };

    const onDateRender = (e) => {
        if (!e.value) {
            return null;
        }
        return formatDate(e.value);
    };

    const onCurrencyRender = (e) => {
        return formatCurrency(e.value, 2);
    };

    const canShowTable = () => {
        return (
            loanId &&
            interestRate &&
            commitment &&
            noteDate &&
            (amortizationTypeId === AmortizationType.Type15 ||
                amortizationTypeId === AmortizationType.Type20 ||
                amortizationTypeId === AmortizationType.Type25 ||
                amortizationTypeId === AmortizationType.Type30)
        );
    };

    const onRowPrepared = (e) => {
        if (e.rowType !== 'data') {
            return;
        }

        if (e.data.isCurrentMonth) {
            const classNames = e.rowElement.className.replace('dx-row-alt', '');
            e.rowElement.className = `${classNames} dx_portfolio_highlight1`;
        }
    };

    const onPageIndexChanged = (e) => {
        set_pageIndex(e);
    };

    if (amortizationTypeId === AmortizationType.TypeIO) {
        return 'Not applicable to interest only loans';
    }

    if (!noteDate) {
        return 'Amortization not available as Note Date not set.';
    }

    if (!interestRate) {
        return 'Amortization not available as Interest Rate not set.';
    }

    return (
        <>
            <label htmlFor='currentBalance'>Balance</label>
            <div style={{ float: 'left', width: '150px' }}>
                <input
                    id='currentBalance'
                    type='text'
                    value={currentBalance}
                    disabled={true}
                />
            </div>
            <button
                type='button'
                className='button hollow'
                style={styles.table_button}
                onClick={(e) => onShowTable(e)}
                disabled={!canShowTable()}
                title='View Amortization Table'
            >
                View
            </button>

            <LoadPanel
                hideOnOutsideClick={false}
                visible={isLoadingOpen}
                message='Generating...'
            />
            <Dialog
                maxWidth='sm'
                open={showTable}
                onClose={_handleClose}
                aria-labelledby='form-dialog-title'
            >
                <div className='modal-container'>
                    <div className='modal-backdrop'></div>
                    <div
                        className='modal'
                        tabIndex='-1'
                        role='dialog'
                        style={{ width: '920px', overflow: 'unset' }}
                    >
                        <div
                            className='modal-dialog'
                            role='document'
                        >
                            <div
                                className='page-header'
                                role='banner'
                            >
                                <h1>Amortization Schedule</h1>
                                <button
                                    type='button'
                                    className='close'
                                    data-dismiss='modal'
                                    aria-label='Close'
                                    onClick={_handleClose}
                                >
                                    <span aria-hidden='true'>&times;</span>
                                </button>
                            </div>
                        </div>
                        <div
                            className='main-content no-search'
                            style={{ padding: '0', width: '100%' }}
                        >
                            <div
                                className='content'
                                style={{ padding: '20px' }}
                            >
                                <DataGrid
                                    dataSource={table}
                                    showBorders={true}
                                    showRowLines={false}
                                    rowAlternationEnabled={true}
                                    remoteOperations={false}
                                    allowColumnReordering={false}
                                    allowColumnResizing={true}
                                    onRowPrepared={onRowPrepared}
                                >
                                    <Column
                                        dataField='pmtNo'
                                        caption='Pmt #'
                                        width={70}
                                    />
                                    <Column
                                        dataField='pmtDate'
                                        caption='Pmt Dt'
                                        width={100}
                                        cellRender={onDateRender}
                                    />
                                    <Column
                                        dataField='startBalance'
                                        caption='Beg. Balance'
                                        width={100}
                                        alignment='right'
                                        cellRender={onCurrencyRender}
                                    />
                                    <Column
                                        dataField='pmt'
                                        caption='Payment'
                                        width={100}
                                        alignment='right'
                                        cellRender={onCurrencyRender}
                                    />
                                    <Column
                                        dataField='extraPmt'
                                        caption='Extra Pmt'
                                        width={100}
                                        alignment='right'
                                        cellRender={onCurrencyRender}
                                    />
                                    <Column
                                        dataField='principal'
                                        caption='Principal'
                                        width={100}
                                        alignment='right'
                                        cellRender={onCurrencyRender}
                                    />
                                    <Column
                                        dataField='interest'
                                        caption='Interest'
                                        width={100}
                                        alignment='right'
                                        cellRender={onCurrencyRender}
                                    />
                                    <Column
                                        dataField='endBalance'
                                        caption='End Balance'
                                        width={100}
                                        alignment='right'
                                        cellRender={onCurrencyRender}
                                    />
                                    <Column
                                        dataField='cumInterest'
                                        caption='Cum. Interest'
                                        width={100}
                                        alignment='right'
                                        cellRender={onCurrencyRender}
                                    />
                                    <Paging
                                        defaultPageSize={pageSize}
                                        pageIndex={pageIndex}
                                        onPageIndexChange={onPageIndexChanged}
                                    />
                                    <Pager
                                        showPageSizeSelector={false}
                                        allowedPageSizes={[pageSize]}
                                        showNavigationButtons={true}
                                        showInfo={true}
                                        visible={true}
                                    />
                                </DataGrid>
                            </div>
                            <div
                                className='button-footer'
                                style={styles.buttonContainer}
                            >
                                <button
                                    type='button'
                                    className='button'
                                    style={styles.button}
                                    onClick={_handleClose}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
        </>
    );
};
